'use client'

import type { RefObject } from 'react'
import { CHARACTER_THEMES } from '@/lib/character-themes'
import type { TrackerCopy, TrackerLocale, TrackerState, SleighPosition } from '@/lib/santa-tracker'
import { TrackerControls, type TrackerControlsProps } from './tracker-controls'
import { TrackerDetailPanel, type TrackerSelection } from './tracker-detail-panel'
import { TrackerMap } from './tracker-map'

/**
 * El bloque del mapa: título, controles, lienzo, panel de detalle, leyenda y la
 * nota para madres y padres.
 *
 * El contenedor del lienzo es el elemento que se pone a **pantalla completa**
 * (de ahí el `ref` que sube al padre), así que la nota y la leyenda quedan
 * fuera a propósito: en pantalla completa lo que se quiere es el mapa.
 */

const LEGEND_DOT = 'mr-1.5'

const SANTA_ACCENT = CHARACTER_THEMES.santa.accent

export interface TrackerMapSectionProps {
  copy: TrackerCopy
  locale: TrackerLocale
  state: TrackerState
  trail: readonly SleighPosition[]
  visitedPath: string
  selection: TrackerSelection
  statusMessage: string
  mapDescription: string
  follow: boolean
  animate: boolean
  auroraAwake: boolean
  controls: TrackerControlsProps
  mapRef: RefObject<HTMLDivElement | null>
  onSelectStop: (stopId: string) => void
  onSelectSleigh: () => void
  onTapNorthPole: () => void
  onCloseDetail: () => void
}

export function TrackerMapSection({
  copy,
  locale,
  state,
  trail,
  visitedPath,
  selection,
  statusMessage,
  mapDescription,
  follow,
  animate,
  auroraAwake,
  controls,
  mapRef,
  onSelectStop,
  onSelectSleigh,
  onTapNorthPole,
  onCloseDetail,
}: TrackerMapSectionProps) {
  const legend = [
    { key: 'visited', label: copy.map.legend.visited, tone: 'text-amber-300' },
    { key: 'current', label: copy.map.legend.current, tone: 'text-white' },
    { key: 'upcoming', label: copy.map.legend.upcoming, tone: 'text-sky-300/60' },
  ]

  return (
    <section id="tracker-map" aria-labelledby="tracker-map-heading" className="space-y-4">
      <div className="flex flex-col gap-2">
        <h2 id="tracker-map-heading" className="font-christmas text-2xl font-semibold md:text-3xl">
          {copy.map.heading}
        </h2>
        <p className="text-base text-white/75">{copy.map.subheading}</p>
      </div>

      <TrackerControls {...controls} />

      {/* El lienzo es 360×152 (2,37:1), así que el contenedor se le acerca en
          móvil: con una caja más alta el mapa se centra y deja franjas de cielo
          vacío arriba y abajo, y en una pantalla estrecha eso es justo el
          espacio que hace falta. */}
      <div
        ref={mapRef}
        className="relative aspect-[2.1/1] w-full overflow-hidden rounded-3xl border border-white/10 bg-[radial-gradient(1200px_420px_at_50%_-15%,rgba(56,189,248,0.22),transparent_60%),linear-gradient(180deg,#030a1c_0%,#071634_45%,#0a1f42_100%)] shadow-2xl sm:aspect-[2.2/1] lg:aspect-[12/5]"
      >
        <TrackerMap
          copy={copy}
          state={state}
          trail={trail}
          visitedPath={visitedPath}
          selectedStopId={selection?.kind === 'stop' ? selection.stopId : null}
          follow={follow}
          animate={animate}
          auroraBoost={auroraAwake}
          onSelectStop={onSelectStop}
          onSelectSleigh={onSelectSleigh}
          onTapNorthPole={onTapNorthPole}
          description={mapDescription}
        />

        <TrackerDetailPanel
          selection={selection}
          copy={copy}
          locale={locale}
          state={state}
          statusMessage={statusMessage}
          onClose={onCloseDetail}
        />
      </div>

      {/* La leyenda va debajo del mapa, no encima: superpuesta tapaba el Polo
          Norte, justo la esquina donde arranca el recorrido. */}
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/70">
        {legend.map((entry) => (
          <li key={entry.key}>
            <span aria-hidden className={`${LEGEND_DOT} ${entry.tone}`}>
              ●
            </span>
            {entry.label}
          </li>
        ))}
      </ul>

      {/* Celebración de cierre: sutil y sin confeti que tape el mapa. Solo
          aparece con el recorrido ya terminado, cuando la página deja de ser
          un seguimiento y pasa a ser un recuerdo. */}
      {state.phase === 'completed' && (
        <p className={`rounded-2xl border px-4 py-3 text-center text-lg font-semibold ${SANTA_ACCENT.note}`}>
          <span aria-hidden className="mr-2">
            ✨
          </span>
          {copy.surprises.celebration}
        </p>
      )}

      {auroraAwake && (
        <p className={`rounded-2xl border px-4 py-3 text-center text-base ${SANTA_ACCENT.note}`}>
          {copy.surprises.northPole}
        </p>
      )}

      <p className="text-xs text-white/55">{copy.map.parentsNote}</p>
    </section>
  )
}
